import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page.tsx';
import ButtonGroup from 'components/shared/ButtonGroup.tsx';
import MobilePageHeader from 'components/shared/MobilePageHeader.tsx';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly.tsx';
import MobileOnly from 'components/shared/responsiveness/MobileOnly.tsx';
import { useDiscardChanges } from 'context/discardChanges.tsx';
import { Form, FormProvider } from 'context/form/index.tsx';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types.ts';
import { TranslationProvider } from 'context/translations.tsx';
import isDeepEqual from 'lib/isDeepEqual.ts';
import Client from 'models/Client.ts';
import { Notification, NotificationOptions } from 'models/Notifications.ts';
import { adminFetchUserNotificationPreferences, adminUsersQueryKeys } from 'services/api/admin/users';
import { clientsQueryKeys, indexClients } from 'services/api/client.ts';
import {
  createNotificationPreferences,
  fetchNotificationPreferences,
  notificationPreferencesQueryKeys,
  updateNotificationPreferences,
} from 'services/api/notificationPreferences.ts';
import { useToastActions } from 'stores/toastStore.ts';
import { H2 } from 'styles/typography.tsx';

import Button from '../shared/Button.tsx';
import Tabs, { TabType } from '../Tabs.tsx';

import ClientSelector from './ClientSelector.tsx';
import NotificationPreferencesContainer from './NotificationPreferencesContainer.tsx';

export type PreferencesFormValues = {
  [groupTypeId: string]: Notification;
};

export type PreferencesFormMeta = {
  isSubmitting: boolean;
};

type NotificationPreferencesProps = {
  user: any;
  isAdmin?: boolean;
  onBack?: () => void;
};

export default function NotificationPreferences({ user, isAdmin = false, onBack }: NotificationPreferencesProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [selectedTab, setSelectedTab] = useState<TabType>();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const actingClientId = user.actingClientId ?? user.actingClient?.id ?? '';
  const effectiveClientId = selectedClient?.id ?? actingClientId;

  // The same client set the Select View (acting client) picker offers: leaf
  // clients only, name-sorted. clientGroupTypes rides along for tab config.
  const clientsParams = { leaf: true, include: 'clientGroupTypes', sortBy: 'name', pageSize: 100 };
  const { data: actableClients } = useQuery({
    queryKey: [...clientsQueryKeys.index, { ...clientsParams, context: 'notificationPreferences' }],
    queryFn: ({ signal }) => indexClients(clientsParams, signal),
    enabled: !isAdmin,
    select: (page) => [...page.data].sort((a, b) => a.name.localeCompare(b.name)),
  });

  // The page is keyed entirely to the selector; the acting client is only the
  // selector's initial value.
  const queryParams = useMemo(
    () => ({
      id: user.id,
      include: 'groupType,scopes',
      client: effectiveClientId,
    }),
    [user.id, effectiveClientId]
  );

  const { data: notificationPreferences } = useQuery({
    queryKey: isAdmin
      ? adminUsersQueryKeys.notificationPreferences(user.id)
      : notificationPreferencesQueryKeys.index(queryParams),
    queryFn: async ({ signal }): Promise<Notification[]> =>
      isAdmin
        ? ((await adminFetchUserNotificationPreferences(user.id, signal)) as unknown as Notification[])
        : fetchNotificationPreferences(queryParams, signal),
    // Never refetch in the background: a refetch replaces the form's
    // initialValues and resets it, silently discarding unsaved edits. The
    // save path keeps this cache correct itself, and switching clients
    // fetches under a fresh key.
    staleTime: Infinity,
  });

  const { mutateAsync: updatePreferences } = useMutation({
    mutationFn: updateNotificationPreferences,
  });

  const { mutateAsync: createPreferences } = useMutation({
    mutationFn: createNotificationPreferences,
    // Key the patch to the client that was SAVED (off the mutation variables):
    // by the time the POST resolves the selector may already point at another
    // client, and patching the live key would merge this client's row — id
    // included — into that client's cache.
    onSuccess: (data, variables) => {
      const savedParams = { id: user.id, include: 'groupType,scopes', client: variables.clientId };

      queryClient.setQueryData<Notification[]>(notificationPreferencesQueryKeys.index(savedParams), (prefs) => {
        return prefs?.map((pref) => {
          if (pref.groupTypeApiName === data.groupTypeApiName) {
            return { ...pref, ...data };
          } else {
            return pref;
          }
        });
      });
    },
  });

  const initialValues = useMemo(() => {
    return notificationPreferences?.reduce((acc, preference) => {
      acc[preference.groupType.id] = preference;
      return acc;
    }, {}) as PreferencesFormValues;
  }, [notificationPreferences]);

  const handleSubmit = async (
    values: FormValues<PreferencesFormValues>,
    _meta: FormMeta<PreferencesFormMeta>,
    helpers: FormHelpers<PreferencesFormValues, PreferencesFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    const requests: Promise<unknown>[] = [];

    for (const key in values) {
      if (isDeepEqual(values[key], initialValues[key])) {
        continue;
      }

      const prefs = {
        ...values[key],
        scopes: {
          groups: values[key]?.scopes.groups?.map((s) => s.id) ?? [],
          planTypes: values[key]?.scopes.planTypes?.map((s) => s.id) ?? [],
          episodes: values[key]?.scopes.episodes?.map((s) => s.id) ?? [],
          caseManagers: values[key]?.scopes.caseManagers?.map((s) => s.id) ?? [],
          utilizationManagers: values[key]?.scopes.utilizationManagers?.map((s) => s.id) ?? [],
          physicianTeams: values[key]?.scopes.physicianTeams?.map((s) => s.id) ?? [],
        },
      } as NotificationOptions;

      if (prefs.id) {
        requests.push(updatePreferences({ ...prefs, urlExtension: `/${prefs.id}`, include: 'groupType,scopes' }));
      } else {
        requests.push(
          createPreferences({
            ...prefs,
            clientId: effectiveClientId,
            include: 'groupType,scopes',
          })
        );
      }
    }

    queryClient.setQueryData<Notification[]>(
      notificationPreferencesQueryKeys.index(queryParams),
      Object.values(values) as Notification[]
    );

    const outcomes = await Promise.allSettled(requests);

    if (outcomes.some((outcome) => outcome.status === 'rejected')) {
      // The optimistic cache write above no longer reflects the server —
      // refetch the truth and tell the user instead of a false success.
      queryClient.invalidateQueries({ queryKey: notificationPreferencesQueryKeys.index(queryParams) });
      addToast({ text: "Some changes couldn't be saved. Please try again." });
      return;
    }

    addToast({ text: 'Your changes have been saved!' });
  };

  const tabs = useMemo(() => {
    // Tabs come straight from the server, which resolves the selected client's
    // enabled types and already honors the user's group-type selections.
    const vals =
      notificationPreferences?.map((preference) => ({
        label: preference.groupType.displayName,
        value: preference.groupType.id,
      })) ?? [];

    // Also re-seat a tab that dropped out of the set (e.g. a refetch shrank
    // the group types) — a stale value renders an empty, disabled form.
    if (!selectedTab || !vals.some((tab) => tab.value === selectedTab.value)) setSelectedTab(vals[0]);
    return vals;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationPreferences]);

  // Whichever client the selector names; user.actingClient only covers the
  // pre-selection state (and the admin view, which loads no client list).
  const configClient =
    selectedClient ?? actableClients?.find((client) => client.id === effectiveClientId) ?? user.actingClient;
  const configForSelectedGroupType = configClient?.configForGroupType(selectedTab?.value || '');
  const caseManagerEnabled = !!configForSelectedGroupType?.caseManager;
  const utilizationManagerEnabled = !!configForSelectedGroupType?.utilizationManager;

  const handleClientChange = (client: Client) => {
    setSelectedClient(client);
    setSelectedTab(undefined);
  };

  const FormActions = ({ dirty, meta }) => {
    const { openDiscardChangesModal } = useDiscardChanges();

    const handleDiscardChanges = () => {
      openDiscardChangesModal({ navigatingAway: false });
    };

    return (
      <StyledButtonGroup>
        <Button
          variant='ghost'
          data-cy='clickable'
          disabled={!dirty || meta.isSubmitting}
          onClick={handleDiscardChanges}>
          Discard Changes
        </Button>
        <Button data-cy='clickable' disabled={!dirty} loading={meta.isSubmitting} type='submit'>
          Save Changes
        </Button>
      </StyledButtonGroup>
    );
  };

  const showClientSelector = !isAdmin && (actableClients?.length ?? 0) > 1;

  return (
    <TranslationProvider groupType={selectedTab && initialValues?.[selectedTab.value]?.groupType}>
      <FormProvider<PreferencesFormValues, PreferencesFormMeta>
        initialValues={initialValues}
        resetOnInitialValuesChange
        withDiscardChanges
        initialMeta={{ isSubmitting: false }}
        onSubmit={isAdmin ? () => {} : handleSubmit}>
        {({ dirty, meta }) => (
          <StyledForm>
            <Page>
              <DesktopOnly>
                <Page.Header>
                  <TopSection>
                    <TitleSection>
                      {isAdmin ? (
                        <>
                          <Page.Title>{user.name}</Page.Title>
                          <Subtitle>
                            {user.actingClient?.name} — {user.role?.name}
                          </Subtitle>
                        </>
                      ) : (
                        <>
                          <Page.Title>Notification Preferences</Page.Title>
                          {showClientSelector ? (
                            <ClientSelector
                              clients={actableClients ?? []}
                              selectedClientId={effectiveClientId}
                              dirty={dirty}
                              onChange={handleClientChange}
                            />
                          ) : (
                            <Subtitle>{(actableClients?.[0] ?? user.actingClient)?.name}</Subtitle>
                          )}
                        </>
                      )}
                    </TitleSection>
                    {isAdmin ? (
                      <Button variant='ghost' onClick={onBack}>
                        Back to Users
                      </Button>
                    ) : (
                      <FormActions {...{ dirty, meta }} />
                    )}
                  </TopSection>
                </Page.Header>
              </DesktopOnly>
              {!isAdmin && (
                <MobileOnly>
                  <StyledMobilePageHeader
                    title='Notification Preferences'
                    actionElement={
                      <StyledSaveButton variant='ghost' type='submit' data-cy='clickable' disabled={!dirty}>
                        Save
                      </StyledSaveButton>
                    }>
                    <Tabs
                      tabs={tabs}
                      onTabClick={setSelectedTab}
                      selectedTab={tabs.find((tab) => tab.value === selectedTab?.value) || tabs[0]}
                    />
                  </StyledMobilePageHeader>
                </MobileOnly>
              )}
              <Page.Content>
                <ContentRow>
                  <DesktopOnly>
                    <LeftNav>
                      <NavItem $active>Care Coordination</NavItem>
                    </LeftNav>
                  </DesktopOnly>
                  <MainColumn>
                    <DesktopOnly>
                      <TabContainer>
                        <Tabs
                          tabs={tabs}
                          onTabClick={setSelectedTab}
                          selectedTab={tabs.find((tab) => tab.value === selectedTab?.value) || tabs[0]}
                        />
                      </TabContainer>
                    </DesktopOnly>
                    <ScrollArea>
                      {selectedTab && (
                        <NotificationPreferencesContainer
                          clientId={effectiveClientId}
                          caseManagerEnabled={caseManagerEnabled}
                          utilizationManagerEnabled={utilizationManagerEnabled}
                          selectedGroupType={selectedTab}
                          canActAsManager={user.canActAsManager}
                          disabled={isAdmin}
                        />
                      )}
                    </ScrollArea>
                  </MainColumn>
                </ContentRow>
              </Page.Content>
            </Page>
          </StyledForm>
        )}
      </FormProvider>
    </TranslationProvider>
  );
}

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding: 24px;
`;

const ContentRow = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
  display: flex;
  gap: 24px;
  height: 100%;
  overflow: hidden;
`;

const LeftNav = styled.nav`
  border-right: 1px solid ${({ theme }) => theme.colors.black10};
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  width: 220px;
  padding: 24px 16px;
`;

const NavItem = styled.div<{ $active?: boolean }>`
  border-radius: 6px;
  color: ${({ $active, theme }) => ($active ? theme.colors.blue100 : theme.colors.black100)};
  background: ${({ $active, theme }) => ($active ? theme.colors.blue10 : 'transparent')};
  font-size: 14px;
  font-weight: 400;
  padding: 8px 12px;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  padding-right: 24px;
`;

// The tabs stay put; everything under them is the page's one scrolling region.
const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 24px;
`;

const TabContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  margin-bottom: 24px;
`;

const TitleSection = styled.div``;

const StyledButtonGroup = styled(ButtonGroup)`
  & > button {
    font-size: 16px;
    font-weight: 500;
  }
`;

const Subtitle = styled(H2)`
  color: ${({ theme }) => theme.colors.blue100};
  font-size: 16px;
  margin-top: 12px;
`;

const StyledSaveButton = styled(Button)`
  padding: 0;
  color: ${({ disabled, theme }) => (disabled ? theme.colors.blue25 : theme.colors.blue100)};
  font-size: 14px;
`;

const StyledMobilePageHeader = styled(MobilePageHeader)`
  padding-bottom: 0px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
